import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, query, orderBy, limit, getDocs, onSnapshot, where, doc, getDoc } from 'firebase/firestore';

import { db, GEMINI_API_KEY } from '../config/firebase';
import { parsePartyDescriptionForFilters } from '../utils/helpers';
import SEO from '../components/shared/SEO';
import MembershipBadge from '../components/shared/MembershipBadge';
import LocationSearchInput from '../components/ui/LocationSearchInput';
import ListingCard from '../components/ui/ListingCard';

export default function Home({ user, navigateTo, isMapsScriptLoaded, isAuthReady, db }) {
    const [keywords, setKeywords] = useState(() => sessionStorage.getItem('hostit_home_keywords') || '');
    const [location, setLocation] = useState(() => sessionStorage.getItem('hostit_home_location') || '');
    const [partyDescription, setPartyDescription] = useState(() => sessionStorage.getItem('hostit_home_partyDesc') || ''); 
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [error, setError] = useState('');
    const [userCity, setUserCity] = useState(null); 

    const [activePersona, setActivePersona] = useState('renter');

    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        sessionStorage.setItem('hostit_home_keywords', keywords);
    }, [keywords]);

    useEffect(() => {
        sessionStorage.setItem('hostit_home_location', location);
    }, [location]);

    useEffect(() => {
        sessionStorage.setItem('hostit_home_partyDesc', partyDescription);
    }, [partyDescription]);

    useEffect(() => {
        if (isMapsScriptLoaded && navigator.geolocation) {
            try {
                navigator.geolocation.getCurrentPosition((position) => {
                    const { latitude, longitude } = position.coords;
                    
                    if (window.google && window.google.maps) {
                        const geocoder = new window.google.maps.Geocoder();
                        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                            if (status === 'OK' && results[0]) {
                                const cityComponent = results[0].address_components.find(c => c.types.includes('locality'));
                                if (cityComponent) {
                                    setUserCity(cityComponent.long_name);
                                }
                            }
                        });
                    }
                }, (err) => {
                    if (err.code !== err.PERMISSION_DENIED) {
                        console.log("Location access denied or failed:", err.message);
                    }
                });
            } catch (e) {
                // Ignore errors
            }
        }
    }, [isMapsScriptLoaded]);

    const commonTerms = [
        'Wedding', 'Birthday', 'Corporate', 'Tent', 'Chair', 'Table',
        'Bounce House', 'DJ', 'Speaker', 'Microphone', 'Projector',
        'Lighting', 'Dance Floor', 'Bar', 'Catering', 'Photo Booth',
        'Decor', 'Flowers', 'Linens', 'Glassware', 'Flatware',
        'Arch', 'Backdrop', 'Generator', 'Heater', 'Fan', 'Venues',
        'Tableware', 'Mobile Bars', 'Canopies', 'Entertainment', 'Audio-Visual', 'Photography'
    ];

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    if (user && user.isVendor) {
        return <VendorHome user={user} navigateTo={navigateTo} db={db} />;
    }

    const handleKeywordChange = (e) => {
        const value = e.target.value;
        setKeywords(value);
        if (value.length > 1) {
            const filtered = commonTerms.filter(term => 
                term.toLowerCase().includes(value.toLowerCase())
            );
            setSuggestions(filtered);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setKeywords(suggestion);
        setShowSuggestions(false);
    };

    const handleSearch = () => {
        navigateTo('listings', { keywords, location, radius: 50 });
    };

    const handleAiSearch = async () => {
        setIsAiLoading(true);
        setError('');
        try {
            const filters = await parsePartyDescriptionForFilters(partyDescription);
            
            let searchMessage = filters.searchSummary || `We searched for "${filters.keywords}"`;
            
            if (!filters.searchSummary && filters.location) {
                searchMessage += ` in ${filters.location}.`;
            }

            navigateTo('listings', {
                keywords: filters.keywords,
                category: filters.category,
                location: filters.location,
                radius: 50,
                aiSearched: true,
                searchMessage: searchMessage 
            });
        } catch (err) {
            console.error("AI Search failed:", err);
            setError("Sorry, the AI search isn't working right now. Please try a manual search.");
        } finally {
            setIsAiLoading(false);
        }
    };

    const categories = [
        { 
            name: 'Venues', 
            slug: 'venues', 
            colorClass: 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white',
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 md:mb-4 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-5a2 2 0 012-2h2a2 2 0 012 2v5" /></svg>
        },
        { 
            name: 'Photo Booths', 
            slug: 'photography', 
            colorClass: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white',
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 md:mb-4 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
        },
        { 
            name: 'Tables & Chairs', 
            slug: 'tables-chairs', 
            colorClass: 'bg-orange-50 text-orange-600 hover:bg-orange-600 hover:text-white',
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 md:mb-4 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 10h18M5 10v10M19 10v10M8 10V5a2 2 0 012-2h4a2 2 0 012 2v5" /></svg>
        },
        { 
            name: 'Decorations', 
            slug: 'decorations', 
            colorClass: 'bg-pink-50 text-pink-600 hover:bg-pink-600 hover:text-white',
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 md:mb-4 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 2l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" /></svg>
        },
        { 
            name: 'Entertainment', 
            slug: 'entertainment', 
            colorClass: 'bg-purple-50 text-purple-600 hover:bg-purple-600 hover:text-white',
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 md:mb-4 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 18V5l12-2v13M9 9h12M6 18a3 3 0 100-6 3 3 0 000 6zM18 16a3 3 0 100-6 3 3 0 000 6z" /></svg>
        },
        { 
            name: 'Mobile Bars', 
            slug: 'mobile-bars', 
            colorClass: 'bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white',
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 md:mb-4 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 22h8M12 15v7M3 3l6 12h6l6-12H3z" /></svg>
        }
    ];

    const popularTags = [
        { label: 'Tents', filter: { category: 'tents-canopies' } },
        { label: 'Tables', filter: { category: 'tables-chairs' } },
        { label: 'DJs', filter: { category: 'djs' } },
        { label: 'Photo Booths', filter: { keywords: 'Photo Booths' } },
        { label: 'Venues', filter: { category: 'venues' } }
    ];

    return (
        <>
            <SEO 
                title="Party Rentals Made Simple" 
                description="Find and book the perfect party rentals near you. From tents and tables to DJs and decor, plan your event with Host-it!" 
            />

            <div className="pt-24 pb-8 bg-white border-b border-gray-100">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-xl md:text-3xl font-serif font-bold text-gray-900 mb-6">What brings you to Host-it! today?</h2>
                    <div className="flex w-full max-w-2xl mx-auto bg-gray-100 p-1.5 rounded-2xl md:rounded-full border border-gray-200 shadow-inner">
                        <button onClick={() => setActivePersona('renter')} className={`flex-1 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-6 py-2 md:py-3 rounded-xl md:rounded-full text-[11px] sm:text-sm md:text-base font-bold transition-all duration-300 ${activePersona === 'renter' ? 'bg-white text-pink-600 shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'}`}>
                            <span className="text-xl md:text-base leading-none">🎉</span> <span>Plan a Party</span>
                        </button>
                        <button onClick={() => setActivePersona('vendor')} className={`flex-1 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-6 py-2 md:py-3 rounded-xl md:rounded-full text-[11px] sm:text-sm md:text-base font-bold transition-all duration-300 ${activePersona === 'vendor' ? 'bg-white text-blue-600 shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'}`}>
                            <span className="text-xl md:text-base leading-none">🏪</span> <span>Sell Rentals</span>
                        </button>
                        <button onClick={() => setActivePersona('pro')} className={`flex-1 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-6 py-2 md:py-3 rounded-xl md:rounded-full text-[11px] sm:text-sm md:text-base font-bold transition-all duration-300 ${activePersona === 'pro' ? 'bg-white text-purple-600 shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'}`}>
                            <span className="text-xl md:text-base leading-none">✨</span> <span>Event Pros</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* RENTER TAB */}
            {activePersona === 'renter' && (
                <div className="animate-fade-in">
                    <div className="relative w-full bg-white py-20 lg:py-32 border-b border-gray-100">
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-[1920px]">
                                <div className="absolute top-[-20%] right-[10%] w-[500px] h-[500px] bg-pink-100/60 rounded-full blur-[80px] mix-blend-multiply"></div>
                                <div className="absolute bottom-[-10%] left-[5%] w-[600px] h-[600px] bg-purple-100/50 rounded-full blur-[100px] mix-blend-multiply"></div>
                            </div>
                        </div>
                        
                        <div className="container mx-auto px-4 sm:px-6 relative z-10 flex flex-col lg:flex-row items-center gap-16">
                            <div className="flex-1 text-center lg:text-left max-w-3xl mx-auto lg:mx-0">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-50 border border-pink-100 text-pink-600 text-xs font-bold uppercase tracking-widest mb-6 shadow-sm">
                                    <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse"></span>
                                    The New Way to Party
                                </div>
                                <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 leading-[1.05] mb-6 font-serif tracking-tighter">
                                    Plan your perfect party. <br/>
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600 italic font-medium pr-2">Without the stress.</span>
                                </h1>
                                <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed font-light">
                                    Discover local vendors, book everything from tents to DJs, and manage your entire event in one seamless dashboard.
                                </p>
                                
                                <div className="bg-white rounded-3xl md:rounded-full p-2.5 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-gray-200/80 max-w-4xl mx-auto lg:mx-0 flex flex-col md:flex-row items-center gap-2 transition-all hover:shadow-[0_8px_30px_rgb(236,72,153,0.12)] focus-within:ring-4 focus-within:ring-pink-500/10 focus-within:border-pink-300" ref={wrapperRef}>
                                    <div className="flex-grow relative w-full md:w-5/12">
                                        <div className="absolute top-1/2 left-5 transform -translate-y-1/2 text-pink-500">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="What do you need?"
                                            value={keywords}
                                            onChange={handleKeywordChange}
                                            onFocus={() => keywords.length > 1 && setShowSuggestions(true)}
                                            className="w-full p-4 pl-12 border-none focus:ring-0 text-gray-900 placeholder-gray-400 h-14 bg-transparent text-base md:text-lg font-medium"
                                        />
                                        {showSuggestions && suggestions.length > 0 && (
                                            <ul className="absolute z-50 w-full bg-white shadow-2xl max-h-60 overflow-y-auto rounded-2xl top-full left-0 border border-gray-100 text-left mt-4 py-2">
                                                {suggestions.map((suggestion, index) => (
                                                    <li 
                                                        key={index}
                                                        onClick={() => handleSuggestionClick(suggestion)}
                                                        className="px-5 py-3 hover:bg-gray-50 cursor-pointer text-gray-700 font-medium transition-colors"
                                                    >
                                                        {suggestion}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <div className="hidden md:block w-px h-10 bg-gray-200 mx-2"></div>

                                    <div className="flex-grow relative w-full md:w-4/12 border-t md:border-t-0 border-gray-100 pt-2 md:pt-0">
                                        <div className="absolute top-1/2 left-5 transform -translate-y-1/2 text-pink-500 z-10 pointer-events-none mt-1 md:mt-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                        <LocationSearchInput 
                                            initialValue={location} 
                                            onPlaceSelected={setLocation} 
                                            isFormInput={false} 
                                            isMapsScriptLoaded={isMapsScriptLoaded}
                                            placeholder={userCity ? `Near ${userCity}` : "City or Zip"}
                                            className="w-full p-4 pl-12 border-none focus:ring-0 text-gray-900 placeholder-gray-400 h-14 bg-transparent text-base md:text-lg font-medium"
                                        />
                                    </div>

                                    <button onClick={handleSearch} className="w-full md:w-auto bg-gray-900 text-white px-10 py-4 font-bold hover:bg-black transition-colors rounded-2xl md:rounded-full shadow-md flex-shrink-0 text-base md:text-lg">
                                        Search
                                    </button>
                                </div>

                                <div className="mt-8 flex flex-wrap gap-2 justify-center lg:justify-start items-center text-sm">
                                    <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mr-2">Trending:</span>
                                    {popularTags.map(tag => (
                                        <button 
                                            key={tag.label} 
                                            onClick={() => navigateTo('listings', tag.filter)}
                                            className="border border-gray-200 bg-white text-gray-600 px-4 py-1.5 rounded-full hover:border-pink-300 hover:text-pink-600 hover:bg-pink-50 transition shadow-sm font-medium text-xs md:text-sm"
                                        >
                                            {tag.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex-1 hidden lg:flex justify-end items-center w-full relative h-[600px]">
                                <div className="relative w-full max-w-lg">
                                    <div className="absolute inset-0 bg-gradient-to-tr from-pink-200 to-purple-200 rounded-[2rem] transform translate-x-6 translate-y-6 opacity-40"></div>
                                    <img 
                                        src="https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                        alt="Beautiful party setup" 
                                        className="relative rounded-[2rem] shadow-2xl z-10 object-cover h-[540px] w-full border-8 border-white" 
                                    />
                                    
                                    <div className="absolute top-12 -left-20 bg-white/95 backdrop-blur-xl p-4 rounded-2xl shadow-xl z-20 flex items-center gap-4 animate-fade-in-up border border-white/50" style={{ animationDelay: '0.2s' }}>
                                        <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-pink-200 text-pink-600 rounded-full flex items-center justify-center text-2xl shadow-inner border border-pink-50">📸</div>
                                        <div className="pr-3">
                                            <p className="text-sm font-extrabold text-gray-900">Photo Booth</p>
                                            <p className="text-xs text-gray-500 font-medium">Booked for $250</p>
                                        </div>
                                    </div>
                                    
                                    <div className="absolute bottom-16 -right-10 bg-white/95 backdrop-blur-xl p-4 rounded-2xl shadow-xl z-20 flex items-center gap-4 animate-fade-in-up border border-white/50" style={{ animationDelay: '0.6s' }}>
                                        <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 text-purple-600 rounded-full flex items-center justify-center text-2xl shadow-inner border border-purple-50">🎧</div>
                                        <div className="pr-3">
                                            <p className="text-sm font-extrabold text-gray-900">Local DJ</p>
                                            <p className="text-xs text-emerald-600 font-bold flex items-center gap-1"><span>✓</span> Available today</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="py-24 bg-gray-50/50">
                        <div className="container mx-auto px-6 max-w-7xl">
                            <div className="text-center mb-16">
                                <h2 className="text-3xl md:text-5xl font-bold text-gray-900 font-serif mb-4 tracking-tight">The modern way to host.</h2>
                                <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed font-light">
                                    Host-it! combines powerful AI planning tools with a curated marketplace of local rentals.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="group p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-pink-200 transition-all duration-300 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-pink-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                    <div className="w-14 h-14 bg-pink-50 text-pink-600 rounded-2xl flex items-center justify-center text-2xl mb-6 relative z-10 shadow-inner">✨</div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 relative z-10">AI-Powered Planning</h3>
                                    <p className="text-gray-500 leading-relaxed relative z-10 text-sm">
                                        Stuck on ideas? Tell our AI your theme and guest count. It generates a complete equipment list tailored to your vision in seconds.
                                    </p>
                                </div>

                                <div className="group p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-purple-200 transition-all duration-300 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                    <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center text-2xl mb-6 relative z-10 shadow-inner">🛍️</div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 relative z-10">Curated Marketplace</h3>
                                    <p className="text-gray-500 leading-relaxed relative z-10 text-sm">
                                        Browse thousands of unique rentals from verified local vendors. From vintage tableware to marquee tents, find it all in one place.
                                    </p>
                                </div>

                                <div className="group p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all duration-300 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                    <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-2xl mb-6 relative z-10 shadow-inner">💌</div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 relative z-10">Smart Management</h3>
                                    <p className="text-gray-500 leading-relaxed relative z-10 text-sm">
                                        Create beautiful AI e-vites, track RSVPs, manage contracts, and coordinate logistics—all from a single, intuitive dashboard.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-900 py-24 px-6 relative overflow-hidden my-12 mx-4 sm:mx-8 rounded-[2.5rem] shadow-2xl">
                        <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/40 to-pink-900/20 blur-3xl"></div>
                        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none mix-blend-overlay">
                             <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
                                    </pattern>
                                </defs>
                                <rect width="100%" height="100%" fill="url(#grid)" />
                            </svg>
                        </div>
                        <div className="max-w-4xl mx-auto text-center relative z-10">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-pink-300 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
                                Beta Feature
                            </div>
                            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-8 tracking-tight">Describe your dream party. <br/><span className="text-gray-300 font-light italic">We'll build the shopping list.</span></h2>
                            <div className="bg-white/10 backdrop-blur-xl p-2.5 rounded-3xl border border-white/20 mb-8 flex flex-col md:flex-row gap-3 shadow-2xl focus-within:ring-2 focus-within:ring-pink-500/50 transition-all">
                                <textarea 
                                    value={partyDescription}
                                    onChange={(e) => {
                                        setPartyDescription(e.target.value);
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                    placeholder="e.g. I'm hosting a rustic outdoor wedding for 150 guests..."
                                    className="w-full bg-transparent text-white placeholder-gray-400 p-4 border-none focus:ring-0 resize-none overflow-hidden text-lg"
                                    style={{ minHeight: '3.5rem' }}
                                    rows={1}
                                />
                                <button onClick={handleAiSearch} disabled={isAiLoading || !partyDescription.trim()} className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold px-8 py-4 rounded-2xl transition disabled:opacity-50 flex items-center justify-center gap-2 shrink-0 shadow-lg">
                                    {isAiLoading ? 'Thinking...' : '✨ Magic Search'}
                                </button>
                            </div>
                            {error && <p className="text-red-400 text-sm mb-4 bg-red-900/50 inline-block px-4 py-2 rounded-lg">{error}</p>}
                        </div>
                    </div>

                    <div className="py-20 bg-gray-50">
                        <div className="container mx-auto px-4 sm:px-6 max-w-[1920px]">
                            <div className="flex justify-between items-end mb-10">
                                <h2 className="text-3xl md:text-4xl font-bold font-serif text-gray-900 tracking-tight">Explore by Category</h2>
                                <button onClick={() => navigateTo('allCategories')} className="hidden md:block text-pink-600 font-bold hover:underline">View All &rarr;</button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
                                {categories.map(cat => (
                                    <CategoryCard key={cat.slug} category={cat} navigateTo={navigateTo} />
                                ))}
                            </div>
                            <button onClick={() => navigateTo('allCategories')} className="w-full mt-8 md:hidden bg-white border border-gray-300 text-gray-700 px-6 py-3.5 rounded-xl font-bold shadow-sm">View All Categories</button>
                        </div>
                    </div>

                    <PopularListings navigateTo={navigateTo} user={user} userCity={userCity} db={db} />
                </div>
            )}

            {/* VENDOR TAB */}
            {activePersona === 'vendor' && (
                <div className="animate-fade-in bg-gray-50">
                    <VendorBusinessSection navigateTo={navigateTo} />
                    
                    <div className="py-16 border-t border-gray-200 bg-white">
                        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-gray-100 text-center">
                            <div><p className="text-4xl md:text-5xl font-bold text-gray-900 font-serif">0%</p><p className="text-sm text-gray-500 mt-2 font-medium uppercase tracking-wider">Listing Fees</p></div>
                            <div><p className="text-4xl md:text-5xl font-bold text-gray-900 font-serif">24h</p><p className="text-sm text-gray-500 mt-2 font-medium uppercase tracking-wider">Fast Payouts</p></div>
                            <div><p className="text-4xl md:text-5xl font-bold text-gray-900 font-serif">10k+</p><p className="text-sm text-gray-500 mt-2 font-medium uppercase tracking-wider">Active Renters</p></div>
                            <div><p className="text-4xl md:text-5xl font-bold text-gray-900 font-serif">100%</p><p className="text-sm text-gray-500 mt-2 font-medium uppercase tracking-wider">Verified Users</p></div>
                        </div>
                    </div>

                    <div className="bg-gray-50 py-24 px-4 md:px-8 border-t border-gray-200">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center max-w-3xl mx-auto mb-16">
                                <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">Run your rental business on autopilot</h2>
                                <p className="text-xl text-gray-600 leading-relaxed">We handle the tech, payments, and logistics so you can focus on growing your inventory and building client relationships.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="p-10 rounded-[2rem] bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group">
                                    <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-3xl mb-8 group-hover:scale-110 transition-transform">💳</div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Instant Payouts</h3>
                                    <p className="text-gray-600 leading-relaxed">Get paid securely via Stripe as soon as a booking is completed. No more chasing invoices, net-30 terms, or dealing with cash.</p>
                                </div>
                                <div className="p-10 rounded-[2rem] bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group">
                                    <div className="w-16 h-16 bg-pink-50 text-pink-600 rounded-2xl flex items-center justify-center text-3xl mb-8 group-hover:scale-110 transition-transform">📅</div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Smart Calendar</h3>
                                    <p className="text-gray-600 leading-relaxed">Track inventory availability across all your items, set buffer days for cleaning, and automatically prevent double-bookings.</p>
                                </div>
                                <div className="p-10 rounded-[2rem] bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group">
                                    <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center text-3xl mb-8 group-hover:scale-110 transition-transform">🗺️</div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Route Optimization</h3>
                                    <p className="text-gray-600 leading-relaxed">Let our AI map out the most efficient delivery route for your weekend drop-offs, saving you time and gas money.</p>
                                </div>
                            </div>
                            <div className="mt-16 text-center">
                                <button onClick={() => navigateTo('signup')} className="bg-gray-900 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-black transition shadow-2xl hover:shadow-gray-900/30 transform hover:-translate-y-1">
                                    Create Your Free Storefront
                                </button>
                                <p className="text-sm text-gray-500 mt-4 font-medium">Takes less than 5 minutes. No credit card required.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* PRO TAB */}
            {activePersona === 'pro' && (
                <div className="animate-fade-in bg-[#0a0a0a]">
                    <div className="relative py-24 md:py-40 px-4 md:px-8 text-center overflow-hidden">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-tr from-purple-900/40 to-pink-900/20 blur-[120px] rounded-full pointer-events-none"></div>
                        
                        <div className="max-w-5xl mx-auto relative z-10">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 font-bold tracking-[0.2em] uppercase text-sm mb-8 block">Host-it! Pro</span>
                            <h2 className="text-5xl md:text-7xl font-serif font-bold mb-8 leading-tight text-white tracking-tight">The operating system for <br/> <span className="italic font-light text-gray-400">top-tier</span> event planners.</h2>
                            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed font-light">Elevate your client experience. Manage multiple events, design CAD seating charts, track budgets, and build your white-label portfolio website in one premium workspace.</p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <button onClick={() => navigateTo('signup')} className="bg-white text-black px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition shadow-[0_0_40px_rgba(255,255,255,0.3)] transform hover:-translate-y-1 w-full sm:w-auto">
                                    Start Planning for Free
                                </button>
                                <button onClick={() => navigateTo('listings')} className="bg-transparent border border-gray-700 text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-900 transition w-full sm:w-auto">
                                    Explore the Marketplace
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-[#111] py-24 px-4 md:px-8 border-t border-gray-800">
                        <div className="max-w-7xl mx-auto">
                            <div className="mb-16 text-center md:text-left">
                                <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">Everything you need to deliver perfection.</h3>
                                <p className="text-gray-400 text-lg">Powerful tools hidden behind a beautifully simple interface.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="md:col-span-2 bg-[#1a1a1a] border border-gray-800 rounded-[2rem] p-8 md:p-12 relative overflow-hidden group hover:border-gray-700 transition-colors">
                                    <div className="relative z-10 w-full md:w-3/5">
                                        <div className="w-14 h-14 bg-purple-500/20 text-purple-400 rounded-2xl flex items-center justify-center text-2xl mb-6">🪑</div>
                                        <h4 className="text-2xl font-bold text-white mb-4">Smart CAD Seating</h4>
                                        <p className="text-gray-400 leading-relaxed mb-8">Drag and drop tables onto a scalable grid. Assign guests dynamically from your imported RSVPs, and use our smart auto-seat algorithm to group families together.</p>
                                    </div>
                                    <div className="absolute right-0 bottom-0 w-1/2 h-full opacity-50 translate-x-1/4 translate-y-1/4 group-hover:scale-105 transition-transform duration-700">
                                        <div className="w-64 h-64 border-4 border-dashed border-gray-700 rounded-full absolute top-10 right-10"></div>
                                        <div className="w-32 h-32 border-4 border-purple-500/50 rounded-lg absolute bottom-20 left-10 rotate-12"></div>
                                    </div>
                                </div>
                                
                                <div className="bg-[#1a1a1a] border border-gray-800 rounded-[2rem] p-8 md:p-10 hover:border-gray-700 transition-colors">
                                    <div className="w-14 h-14 bg-pink-500/20 text-pink-400 rounded-2xl flex items-center justify-center text-2xl mb-6">🎨</div>
                                    <h4 className="text-xl font-bold text-white mb-3">Interactive Mood Boards</h4>
                                    <p className="text-gray-400 leading-relaxed text-sm">Curate color palettes and design inspiration. Link specific marketplace rentals directly to the client's vision board for one-click booking.</p>
                                </div>

                                <div className="bg-[#1a1a1a] border border-gray-800 rounded-[2rem] p-8 md:p-10 hover:border-gray-700 transition-colors">
                                    <div className="w-14 h-14 bg-blue-500/20 text-blue-400 rounded-2xl flex items-center justify-center text-2xl mb-6">🌐</div>
                                    <h4 className="text-xl font-bold text-white mb-3">White-Label Portfolios</h4>
                                    <p className="text-gray-400 leading-relaxed text-sm">Launch a customizable website to showcase your services, display your past event gallery, and capture new leads.</p>
                                </div>

                                <div className="md:col-span-2 bg-[#1a1a1a] border border-gray-800 rounded-[2rem] p-8 md:p-12 hover:border-gray-700 transition-colors flex flex-col md:flex-row items-center gap-8">
                                    <div className="flex-1">
                                        <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-2xl flex items-center justify-center text-2xl mb-6">📊</div>
                                        <h4 className="text-2xl font-bold text-white mb-4">Unified CRM & Budgeting</h4>
                                        <p className="text-gray-400 leading-relaxed">Keep track of every client detail, from initial lead capture to final budget reconciliation. Send contracts, manage invoices, and track payments all in one place.</p>
                                    </div>
                                    <div className="w-full md:w-1/3 bg-[#222] p-4 rounded-xl border border-gray-800">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-xs font-bold text-gray-500 uppercase">Budget</span>
                                            <span className="text-xs text-emerald-400">On Track</span>
                                        </div>
                                        <div className="h-2 w-full bg-gray-800 rounded-full mb-2 overflow-hidden"><div className="h-full bg-emerald-500 w-2/3"></div></div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-white font-bold">$12,400</span>
                                            <span className="text-gray-500">/ $15,000</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function CategoryCard({ category, navigateTo }) {
    const handleClick = () => {
        navigateTo('listings', { category: category.slug, keywords: '', location: '' });
    };

    return (
        <div 
            onClick={handleClick} 
            className={`relative flex flex-col items-center justify-center p-6 rounded-2xl cursor-pointer shadow-sm hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 group border border-gray-100 h-40 md:h-48 ${category.colorClass}`}
        >
            {category.icon}
            <h3 className="text-center font-bold text-sm md:text-base tracking-wide transition-colors">{category.name}</h3>
        </div>
    );
}

function PopularListings({ navigateTo, user, userCity, db }) {
    const [listings, setListings] = useState([]);
    const [vendors, setVendors] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!db) return;
        const fetchPopular = async () => {
            setLoading(true);
            const q = query(collection(db, "listings"), orderBy("wishlistCount", "desc"), limit(50));
            const listingsSnapshot = await getDocs(q);
            let popularListings = listingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (userCity) {
                popularListings.sort((a, b) => {
                    const aLoc = (a.location || '').toLowerCase();
                    const bLoc = (b.location || '').toLowerCase();
                    const cityLower = userCity.toLowerCase();
                    
                    const aMatch = aLoc.includes(cityLower);
                    const bMatch = bLoc.includes(cityLower);

                    if (aMatch && !bMatch) return -1;
                    if (!aMatch && bMatch) return 1;
                    return 0; 
                });
            }

            setListings(popularListings.slice(0, 8));

            if (popularListings.length > 0) {
                const topListings = popularListings.slice(0, 8);
                const vendorIds = [...new Set(topListings.map(l => l.vendorId))];
                const vendorsData = {};
                for (const id of vendorIds) {
                    const vendorSnap = await getDoc(doc(db, "users", id));
                    if (vendorSnap.exists()) {
                        vendorsData[id] = vendorSnap.data();
                    }
                }
                setVendors(vendorsData);
            }
            setLoading(false);
        };
        fetchPopular();
    }, [userCity, db]); 

    if (loading) return <div className="text-center p-10">Finding popular items...</div>;
    if (listings.length === 0) return null;

    return (
        <div className="py-12 bg-gray-100">
            <div className="container mx-auto px-4">
                <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">
                    {userCity ? `Popular in ${userCity}` : "Popular Items"}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {listings.map(listing => (
                        <ListingCard
                            key={listing.id}
                            listing={listing}
                            vendor={vendors[listing.vendorId]}
                            navigateTo={navigateTo}
                            user={user}
                            isFavorite={user && !user.isAnonymous && user.favorites?.includes(listing.id)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export function VendorHome({ user, navigateTo, db }) {
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        if (!user || !db) return;
        const q = query(
            collection(db, "bookings"), 
            where("vendorId", "==", user.uid),
            where("status", "==", "pending")
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingCount(snapshot.size);
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("Pending count listener error:", error);
        });
        return () => unsubscribe();
    }, [user, db]);

    const quickActions = [
        { title: 'Calendar & Orders', desc: 'Track your schedule and upcoming deliveries.', icon: '📅', colorClass: 'bg-pink-50 group-hover:bg-pink-100 text-pink-600', action: () => navigateTo('dashboard') },
        { title: 'Messages', desc: 'Chat with renters and finalize details.', icon: '💬', colorClass: 'bg-purple-50 group-hover:bg-purple-100 text-purple-600', action: () => navigateTo('messages') },
        { title: 'Business Settings', desc: 'Configure payouts, profile, and vacation mode.', icon: '⚙️', colorClass: 'bg-blue-50 group-hover:bg-blue-100 text-blue-600', action: () => navigateTo('settings') }
    ];

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            <div className="bg-gray-900 text-white pt-24 pb-32 px-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-pink-900/40 via-purple-900/40 to-transparent opacity-60 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-2/3 bg-gradient-to-tr from-blue-900/30 to-transparent opacity-40 blur-3xl"></div>
                
                <div className="container mx-auto max-w-6xl relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-12">
                        <div className="flex-1 space-y-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-pink-300 text-xs font-bold uppercase tracking-wider">
                                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                                Vendor Portal
                            </div>
                            
                            <h1 className="text-5xl md:text-6xl font-bold font-serif leading-tight">
                                Good afternoon, <br/>
                                <span className="flex items-center gap-4 flex-wrap mt-2">
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-300">
                                        {user.vendorName || user.firstName || 'Partner'}
                                    </span>
                                    <MembershipBadge user={user} className="text-sm px-3 py-1 shadow-md" />
                                </span>
                            </h1>
                            
                            <p className="text-xl text-gray-300 max-w-lg leading-relaxed mt-4">
                                Manage your rentals, track your schedule, and grow your business all in one place.
                            </p>
                            
                            <div className="flex flex-wrap gap-4 pt-4">
                                <button 
                                    onClick={() => navigateTo('dashboard', { viewedBookingId: 'pending' })} 
                                    className="group bg-pink-600 hover:bg-pink-500 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all shadow-lg shadow-pink-900/20 hover:shadow-pink-600/40 flex items-center gap-3 transform hover:-translate-y-1"
                                >
                                    <span>Manage Orders</span>
                                    {pendingCount > 0 && <span className="bg-white text-pink-600 px-2 py-0.5 rounded-full text-xs">{pendingCount}</span>}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                </button>
                                <button 
                                    onClick={() => navigateTo('myPage')} 
                                    className="bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/30 px-8 py-4 rounded-2xl font-bold text-lg transition-all backdrop-blur-md flex items-center gap-3"
                                >
                                    <span>View Storefront</span>
                                </button>
                            </div>
                        </div>
                        
                        <div className="hidden md:block relative">
                            <div className="w-64 h-64 bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl rotate-6 border border-white/5 shadow-2xl flex items-center justify-center relative z-10 backdrop-blur-sm">
                                <span className="text-7xl">🏪</span>
                            </div>
                            <div className="absolute inset-0 bg-pink-500/20 rounded-3xl -rotate-6 blur-xl scale-95 z-0"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-6 -mt-24 relative z-20 mb-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {quickActions.map((item, idx) => (
                        <div 
                            key={idx}
                            onClick={item.action} 
                            className="bg-white p-8 rounded-3xl shadow-xl hover:shadow-2xl border border-gray-100 cursor-pointer transition-all duration-300 group transform hover:-translate-y-2"
                        >
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors ${item.colorClass}`}>
                                <span className="text-3xl">{item.icon}</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-pink-600 transition-colors">{item.title}</h3>
                            <p className="text-gray-500 leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="container mx-auto px-6 pb-24 max-w-6xl">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-xl">🚀</div>
                        <h2 className="text-2xl font-bold text-gray-900">Grow Your Business</h2>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row gap-6 items-start">
                        <div className="w-20 h-20 bg-gradient-to-br from-yellow-50 to-orange-100 rounded-2xl flex-shrink-0 flex items-center justify-center text-4xl shadow-inner">
                            📸
                        </div>
                        <div>
                            <h4 className="font-bold text-xl text-gray-900 mb-2">Quality Photos Matter</h4>
                            <p className="text-gray-600 leading-relaxed mb-4">Listings with at least 4 high-quality images receive <strong className="text-gray-900">2.5x more booking requests</strong>. Is your inventory ready for its close-up?</p>
                            <button onClick={() => navigateTo('dashboard')} className="text-pink-600 font-bold hover:text-pink-700 hover:underline inline-flex items-center gap-1">
                                Update Listings <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                            </button>
                        </div>
                    </div>
                    
                    <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row gap-6 items-start">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl flex-shrink-0 flex items-center justify-center text-4xl shadow-inner">
                            🏷️
                        </div>
                        <div>
                            <h4 className="font-bold text-xl text-gray-900 mb-2">Boost Sales with Coupons</h4>
                            <p className="text-gray-600 leading-relaxed mb-4">Create a "SUMMER10" or "WELCOME" coupon code to attract new customers and fill up your calendar during slower months.</p>
                            <button onClick={() => navigateTo('dashboard')} className="text-pink-600 font-bold hover:text-pink-700 hover:underline inline-flex items-center gap-1">
                                Create Coupon <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function VendorBusinessSection({ navigateTo }) {
    return (
        <div className="bg-gray-900 py-24 px-4 md:px-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-gray-800 to-transparent opacity-50 transform skew-x-12"></div>
            
            <div className="w-full max-w-[1920px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                <div className="space-y-8">
                    <div>
                        <span className="text-pink-500 font-bold tracking-wider uppercase text-sm">For Rental Businesses</span>
                        <h2 className="text-4xl md:text-5xl font-bold mt-2 font-serif leading-tight">Scale your rental business with <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">Host-it! Pro</span></h2>
                    </div>
                    
                    <p className="text-xl text-gray-300 leading-relaxed">
                        Ditch the spreadsheets. Get a powerful "Business in a Box" solution designed specifically for event rental companies.
                    </p>
                    
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-1">
                                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <div>
                                <h4 className="font-bold text-white">Automated Logistics</h4>
                                <p className="text-gray-400 text-sm">AI-powered delivery routing and inventory conflict detection.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-1">
                                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <div>
                                <h4 className="font-bold text-white">Professional Storefront</h4>
                                <p className="text-gray-400 text-sm">Showcase your inventory with a beautiful, customizable profile page.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-1">
                                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <div>
                                <h4 className="font-bold text-white">Streamlined Payouts</h4>
                                <p className="text-gray-400 text-sm">Get paid directly to your bank account with Stripe integration.</p>
                            </div>
                        </div>
                    </div>

                    <button onClick={() => navigateTo('settings')} className="inline-flex items-center gap-2 bg-pink-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-pink-600 transition shadow-lg hover:shadow-pink-500/25 transform hover:-translate-y-1">
                        Start Selling Today
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                    </button>
                </div>
                
                <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl opacity-30 blur-2xl"></div>
                    <img 
                        src="https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                        alt="Vendor managing inventory" 
                        className="relative rounded-2xl shadow-2xl w-full object-cover h-[500px] border border-gray-700" 
                    />
                    
                    <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl text-gray-800 max-w-xs animate-bounce-slow hidden md:block">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">$</div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-bold">Monthly Revenue</p>
                                <p className="text-xl font-bold">$12,450.00</p>
                            </div>
                        </div>
                        <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                            <div className="bg-green-500 w-3/4 h-full rounded-full"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}